/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */


import {Vue} from 'vue-property-decorator';

function collectChildrenWithClass<V extends Vue>(
    parent: Vue,
    targetList: V[],
    childType: {new(): V},
    limitToNumber?: number
) {
  const children: Array<Vue> = parent.$children;
  for (const child of children) {
    if (child instanceof childType) {
      targetList.push(child as V);
      if (limitToNumber && targetList.length >= limitToNumber) {
        return;
      }
    } else {
      collectChildrenWithClass(child, targetList, childType, limitToNumber);
    }
  }
}

/**
 * Locates a component of the provided class (exact class or a subclass) in the component's
 * parent chain.
 *
 * @param component             a component whose parent should be returned
 * @param parentComponentClass  a class (constructor) that specifies a parent to return
 * @return a parent component with the specified class, if it was found, and `null` if there's no
 *         such component in the parent chain
 */
export function parentWithClass(component: Vue, parentComponentClass: typeof Vue): Vue | null {
  for (let parent = component.$parent; parent; parent = parent.$parent) {
    if (parent instanceof parentComponentClass) {
      return parent;
    }
  }
  return null;
}

/**
 * Traverses the tree of component's children and returns a list of components that are instances
 * of the specified class.
 *
 * @param component   a component whose children should be searched
 * @param childClass  a class (constructor) of the child components that should be found
 */
export function childrenWithClass<V extends Vue>(
    component: Vue,
    childClass: {new(): V}): V[] {
  const targetList: Array<V> = [];
  collectChildrenWithClass(component, targetList, childClass);
  return targetList;
}

/**
 * Locates a child with the specified class (constructor) in the component's child tree.
 *
 * @param component   a component whose children should be searched
 * @param childClass  a class (constructor) of the child component that should be found
 */
export function childWithClass<V extends Vue>(
    component: Vue,
    childClass: typeof Vue): V {
  const targetList: Array<V> = [];
  collectChildrenWithClass(component, targetList, childClass, 1);
  return targetList[0];
}
